/**
 * Hero tooltip map — starfield, capability globes, horizontal tooltips
 */

class HeroTooltipMap {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('.hero-tooltip-map__stage');
    this.media = root.querySelector('[data-htm-media]');
    this.starsCanvas = root.querySelector('[data-htm-stars]');
    this.globesMount = root.querySelector('[data-htm-globes]');
    this.tooltip = root.querySelector('[data-htm-tooltip]');
    this.leader = root.querySelector('[data-htm-leader]');
    this.capsule = root.querySelector('[data-htm-capsule]');
    this.capsuleCategory = root.querySelector('[data-htm-capsule-category]');
    this.capsuleText = root.querySelector('[data-htm-capsule-text]');
    this.capsuleSee = root.querySelector('[data-htm-capsule-see]');
    this.copy =
      root.querySelector('[data-hero-copy]') ||
      root.querySelector('.hero-tooltip-map__content-inner');
    this.ctx = this.starsCanvas?.getContext('2d', { alpha: true });

    this.width = 0;
    this.height = 0;
    this.dpr = 1;
    this.stars = [];
    this.nodes = [];
    this.time = 0;
    this.rafId = null;
    this.scrollRaf = null;
    this.isVisible = true;
    this.isPaused = false;
    this.pointerX = 0;
    this.pointerY = 0;
    this.smoothPointerX = 0;
    this.smoothPointerY = 0;
    this.hasPointer = false;
    this.activeId = null;
    this.leaderProgress = 0;
    this.capsuleProgress = 0;
    this.tooltipReveal = false;
    this.coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDocPointerDown = this.onDocPointerDown.bind(this);
    this.tick = this.tick.bind(this);

    this.readConfig();
    this.setupReducedMotion();
    this.collectNodes();
    this.bindGlobes();
  }

  readConfig() {
    const { dataset } = this.root;
    this.config = {
      starQuantity: Number(dataset.starQuantity) || 140,
      starSizeMin: Number(dataset.starSizeMin) || 0.4,
      starSizeMax: Number(dataset.starSizeMax) || 1.4,
      starSpacingMin: Number(dataset.starSpacingMin) || 30,
      floatSpeed: Number(dataset.floatSpeed) || 0.0007,
      mouseInfluence: Number(dataset.mouseInfluence) || 0.08,
      glowStrength: Number(dataset.glowStrength) || 1.6,
      animationSpeed: Number(dataset.animationSpeed) || 1,
      disableAnimation: dataset.disableAnimation === 'true',
      pauseOffScreen: dataset.pauseOffScreen !== 'false',
      seeTemplate: dataset.seeTemplate || 'See __TITLE__',
      labels: {
        strategy: dataset.labelStrategy || 'Strategy & Planning',
        programme: dataset.labelProgramme || 'Programme & Delivery Leadership',
        technology: dataset.labelTechnology || 'Technology Transformation',
      },
    };
    this.starRgb = this.parseColor(dataset.starColor || '#ffffff');
  }

  setupReducedMotion() {
    const forced = this.root.dataset.reducedMotionOverride === 'true';
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = forced || mq.matches || this.config.disableAnimation;
    this.root.classList.toggle('hero-tooltip-map--reduced-motion', this.reducedMotion);
  }

  parseColor(hex) {
    const value = String(hex || '#ffffff').replace('#', '');
    const normalized =
      value.length === 3
        ? value
            .split('')
            .map((c) => c + c)
            .join('')
        : value;
    return {
      r: parseInt(normalized.slice(0, 2), 16) || 255,
      g: parseInt(normalized.slice(2, 4), 16) || 255,
      b: parseInt(normalized.slice(4, 6), 16) || 255,
    };
  }

  seededRandom(seed) {
    const x = Math.sin(seed * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  }

  collectNodes() {
    const buttons = [...this.root.querySelectorAll('[data-tooltip-globe]')];
    this.nodes = buttons.map((el, index) => {
      const seed = (index + 1) * 17.3;
      return {
        el,
        id: el.dataset.id || `node-${index}`,
        category: el.dataset.category || 'strategy',
        title: el.getAttribute('data-title') || el.dataset.title || '',
        seeTitle: el.getAttribute('data-see-title') || el.dataset.seeTitle || '',
        url: el.dataset.url || '',
        manual:
          el.dataset.posX !== undefined &&
          el.dataset.posY !== undefined &&
          el.hasAttribute('data-pos-x'),
        xPct: el.dataset.posX !== undefined ? Number(el.dataset.posX) : null,
        yPct: el.dataset.posY !== undefined ? Number(el.dataset.posY) : null,
        baseX: 0,
        baseY: 0,
        x: 0,
        y: 0,
        wanderRadius: 10 + this.seededRandom(seed) * 18,
        phaseX: this.seededRandom(seed + 1) * Math.PI * 2,
        phaseY: this.seededRandom(seed + 2) * Math.PI * 2,
        speedMult: 0.35 + this.seededRandom(seed + 3) * 0.7,
      };
    });
  }

  bindGlobes() {
    this.nodes.forEach((node) => {
      node.el.addEventListener('pointerenter', () => {
        if (this.coarsePointer) return;
        this.openTooltip(node);
      });
      node.el.addEventListener('focus', () => this.openTooltip(node));
      node.el.addEventListener('click', (event) => {
        if (this.coarsePointer) {
          event.preventDefault();
          if (this.activeId === node.id) {
            if (node.url) window.location.href = node.url;
            else this.closeTooltip();
          } else {
            this.openTooltip(node);
          }
          return;
        }
        if (node.url) {
          event.preventDefault();
          window.location.href = node.url;
        }
      });
    });
  }

  init() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.root.addEventListener('pointerleave', this.onPointerLeave);
    window.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('pointerdown', this.onDocPointerDown, true);
    this.updateCopyFade();

    if (this.config.pauseOffScreen && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isVisible = Boolean(entry?.isIntersecting);
          if (this.isVisible) this.start();
          else this.stop();
        },
        { threshold: 0.05 }
      );
      this.observer.observe(this.root);
    } else {
      this.start();
    }

    // Show one tooltip on load so the interaction is discoverable.
    if (this.nodes.length) {
      requestAnimationFrame(() => {
        this.openTooltip(this.nodes[0], { instant: this.reducedMotion });
      });
    }
  }

  destroy() {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('pointermove', this.onPointerMove);
    this.root.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('pointerdown', this.onDocPointerDown, true);
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }
    this.resetCopyFade();
    this.observer?.disconnect();
  }

  onPointerMove(event) {
    const rect = this.stage?.getBoundingClientRect() || this.root.getBoundingClientRect();
    this.pointerX = event.clientX - rect.left;
    this.pointerY = event.clientY - rect.top;
    this.hasPointer = true;
  }

  onPointerLeave() {
    this.hasPointer = false;
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.closeTooltip();
  }

  onDocPointerDown(event) {
    if (!this.coarsePointer || !this.activeId) return;
    if (event.target.closest('[data-tooltip-globe]')) return;
    if (event.target.closest('[data-htm-capsule-see]')) return;
    if (event.target.closest('[data-htm-tooltip]')) return;
    this.closeTooltip();
  }

  onResize() {
    const rect = this.stage?.getBoundingClientRect() || this.root.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = rect.width;
    this.height = rect.height;

    this.starsCanvas.width = Math.round(this.width * this.dpr);
    this.starsCanvas.height = Math.round(this.height * this.dpr);
    this.starsCanvas.style.width = `${this.width}px`;
    this.starsCanvas.style.height = `${this.height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.layoutNodes();
    this.generateStars();
    this.updateCopyFade();

    if (this.activeId) {
      const active = this.nodes.find((n) => n.id === this.activeId);
      if (active) this.positionTooltip(active);
    }
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateCopyFade();
    });
  }

  readCssPx(value) {
    const parsed = Number.parseFloat(String(value || '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  resetCopyFade() {
    if (!this.copy) return;
    this.copy.style.removeProperty('--hero-copy-opacity');
    this.copy.style.removeProperty('pointer-events');
  }

  updateCopyFade() {
    if (!this.copy) return;

    if (
      this.reducedMotion ||
      window.matchMedia('(max-width: 989px)').matches
    ) {
      this.resetCopyFade();
      return;
    }

    const rootRect = this.root.getBoundingClientRect();

    if (rootRect.bottom <= 0 || rootRect.top >= window.innerHeight) {
      this.copy.style.setProperty('--hero-copy-opacity', '0');
      this.copy.style.pointerEvents = 'none';
      return;
    }

    // Fade from how far the section has scrolled up — guarantees full
    // opacity at the rest position and a clean restore on scroll-back.
    const scrolled = Math.max(0, -rootRect.top);
    const fadeDistance = Math.max(96, Math.min(200, window.innerHeight * 0.22));
    const opacity = 1 - Math.min(1, scrolled / fadeDistance);

    this.copy.style.setProperty('--hero-copy-opacity', opacity.toFixed(3));
    if (opacity < 0.08) {
      this.copy.style.pointerEvents = 'none';
    } else {
      this.copy.style.removeProperty('pointer-events');
    }
  }

  layoutNodes() {
    const padX = 0.08;
    const padBottom = 0.1;
    const safeTopPx = this.getContentSafeTop();
    const padTop = Math.min(0.7, safeTopPx / this.height);
    const usable = Math.max(0.12, 1 - padTop - padBottom);
    const minDist = Math.max(48, Math.min(this.width, this.height) * 0.055);
    const placed = [];

    this.nodes.forEach((node, index) => {
      if (node.manual && Number.isFinite(node.xPct) && Number.isFinite(node.yPct)) {
        node.baseX = (node.xPct / 100) * this.width;
        node.baseY = (node.yPct / 100) * this.height;
      } else {
        let x = 0;
        let y = 0;
        let ok = false;
        for (let attempt = 0; attempt < 60; attempt += 1) {
          const seed = (index + 1) * 19.17 + attempt * 7.3;
          x =
            (padX + this.seededRandom(seed) * (1 - padX * 2)) * this.width;
          y = (padTop + this.seededRandom(seed + 1) * usable) * this.height;
          ok = placed.every((p) => {
            const dx = p.x - x;
            const dy = p.y - y;
            return dx * dx + dy * dy >= minDist * minDist;
          });
          if (ok) break;
        }
        node.baseX = x;
        node.baseY = y;
        node.xPct = (x / this.width) * 100;
        node.yPct = (y / this.height) * 100;
      }

      // Keep even manual placements out of the headline band.
      node.baseY = Math.max(node.baseY, safeTopPx + 8);
      node.x = node.baseX;
      node.y = node.baseY;
      placed.push({ x: node.baseX, y: node.baseY });
      this.applyNodePosition(node);
    });
  }

  getContentSafeTop() {
    if (!this.height) return 0;
    const fallback = this.height * 0.48;
    const blocks = this.root.querySelector('.hero-tooltip-map__blocks');
    if (!blocks || !this.stage) return fallback;

    const stageRect = this.stage.getBoundingClientRect();
    const blocksRect = blocks.getBoundingClientRect();
    if (!stageRect.height || !blocksRect.height) return fallback;

    const contentBottom = blocksRect.bottom - stageRect.top;
    return Math.min(
      this.height * 0.72,
      Math.max(fallback, contentBottom + 28)
    );
  }

  applyNodePosition(node) {
    if (!this.width || !this.height) return;
    node.el.style.setProperty('--globe-x', `${(node.x / this.width) * 100}%`);
    node.el.style.setProperty('--globe-y', `${(node.y / this.height) * 100}%`);
  }

  updateGlobeDrift() {
    if (this.reducedMotion || !this.width || !this.height) return;

    const t = this.time * this.config.floatSpeed * this.config.animationSpeed * 0.85;
    const safeTop = this.getContentSafeTop();
    let active = null;

    this.nodes.forEach((node) => {
      const time = t * node.speedMult;
      const driftX =
        Math.sin(time + node.phaseX) * node.wanderRadius +
        Math.sin(time * 1.35 + node.phaseY) * node.wanderRadius * 0.35;
      const driftY =
        Math.cos(time * 0.9 + node.phaseY) * node.wanderRadius +
        Math.cos(time * 1.2 + node.phaseX) * node.wanderRadius * 0.3;

      node.x = node.baseX + driftX;
      node.y = Math.max(safeTop + 8, node.baseY + driftY);
      this.applyNodePosition(node);

      if (node.id === this.activeId) active = node;
    });

    if (active) this.positionTooltip(active);
  }

  generateStars() {
    if (!this.ctx) return;
    const area = this.width * this.height;
    const idealSpacing = Math.sqrt(area / (this.config.starQuantity * 0.85));
    const minDist = Math.max(this.config.starSpacingMin, idealSpacing);
    const points = this.poissonDiscSample(
      this.width,
      this.height,
      minDist,
      this.config.starQuantity
    );

    this.stars = points.map((point, index) => {
      const seed = (index + 1) * 91.7;
      const depth = 0.25 + this.seededRandom(seed) * 0.75;
      const t = this.seededRandom(seed + 1);
      const radius =
        this.config.starSizeMin +
        t * (this.config.starSizeMax - this.config.starSizeMin);
      return {
        baseX: point.x,
        baseY: point.y,
        radius,
        opacity: 0.25 + this.seededRandom(seed + 2) * 0.55,
        depth,
        wanderRadius: 14 + this.seededRandom(seed + 3) * 36,
        phaseX: this.seededRandom(seed + 4) * Math.PI * 2,
        phaseY: this.seededRandom(seed + 5) * Math.PI * 2,
        speedMult: 0.45 + this.seededRandom(seed + 6) * 0.9,
        offsetX: 0,
        offsetY: 0,
        currentOffsetX: 0,
        currentOffsetY: 0,
        targetOffsetX: 0,
        targetOffsetY: 0,
      };
    });
  }

  poissonDiscSample(width, height, minDist, maxPoints) {
    const cellSize = minDist / Math.SQRT2;
    const cols = Math.ceil(width / cellSize) || 1;
    const rows = Math.ceil(height / cellSize) || 1;
    const grid = new Array(cols * rows);
    const points = [];
    const active = [];
    const margin = minDist * 0.35;

    const place = (x, y) => {
      const point = { x, y };
      points.push(point);
      active.push(point);
      const col = Math.floor(x / cellSize);
      const row = Math.floor(y / cellSize);
      grid[col + row * cols] = point;
    };

    place(width * 0.5, height * 0.5);

    while (active.length && points.length < maxPoints) {
      const idx = Math.floor(Math.random() * active.length);
      const center = active[idx];
      let found = false;

      for (let i = 0; i < 20; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const radius = minDist * (1 + Math.random());
        const x = center.x + Math.cos(angle) * radius;
        const y = center.y + Math.sin(angle) * radius;
        if (x < margin || y < margin || x >= width - margin || y >= height - margin) {
          continue;
        }

        const col = Math.floor(x / cellSize);
        const row = Math.floor(y / cellSize);
        let ok = true;
        for (let ox = -2; ox <= 2 && ok; ox += 1) {
          for (let oy = -2; oy <= 2; oy += 1) {
            const c = col + ox;
            const r = row + oy;
            if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
            const neighbor = grid[c + r * cols];
            if (!neighbor) continue;
            const dx = neighbor.x - x;
            const dy = neighbor.y - y;
            if (dx * dx + dy * dy < minDist * minDist) {
              ok = false;
              break;
            }
          }
        }
        if (!ok) continue;
        place(x, y);
        found = true;
        break;
      }

      if (!found) active.splice(idx, 1);
    }

    return points;
  }

  formatSeeLabel(title) {
    return String(this.config.seeTemplate || 'See __TITLE__').replace(
      /__TITLE__|\{\{\s*title\s*\}\}/g,
      title
    );
  }

  openTooltip(node, options = {}) {
    const switching = this.activeId && this.activeId !== node.id;
    this.activeId = node.id;
    this.tooltipReveal = true;
    this.nodes.forEach((n) => {
      n.el.classList.toggle('is-active', n.id === node.id);
      n.el.classList.toggle('is-dimmed', n.id !== node.id);
      n.el.setAttribute('aria-expanded', n.id === node.id ? 'true' : 'false');
    });

    this.root.style.setProperty(
      '--htm-tooltip-tone',
      getComputedStyle(node.el).getPropertyValue('--globe-tone').trim() ||
        'var(--htm-tone-strategy)'
    );

    const categoryLabel =
      this.config.labels[node.category] || this.config.labels.strategy || '';
    if (this.capsuleCategory) {
      this.capsuleCategory.textContent = categoryLabel;
      this.capsuleCategory.hidden = !categoryLabel;
    }
    this.capsuleText.textContent = node.title;

    if (this.capsuleSee) {
      if (node.seeTitle) {
        this.capsuleSee.hidden = false;
        this.capsuleSee.textContent = this.formatSeeLabel(node.seeTitle);
        if (node.url) {
          this.capsuleSee.href = node.url;
          this.capsuleSee.removeAttribute('aria-disabled');
        } else {
          this.capsuleSee.removeAttribute('href');
          this.capsuleSee.setAttribute('aria-disabled', 'true');
        }
      } else {
        this.capsuleSee.hidden = true;
        this.capsuleSee.textContent = '';
        this.capsuleSee.removeAttribute('href');
      }
    }

    this.tooltip.hidden = false;
    this.tooltip.classList.add('is-open');

    if (options.instant || this.reducedMotion) {
      this.leaderProgress = 1;
      this.capsuleProgress = 1;
    } else if (!switching) {
      this.leaderProgress = 0;
      this.capsuleProgress = 0;
    } else {
      // Keep capsule visible while swapping; redraw the leader briefly.
      this.leaderProgress = 0;
      this.capsuleProgress = Math.max(this.capsuleProgress, 0.85);
    }

    this.positionTooltip(node);
  }

  closeTooltip() {
    this.activeId = null;
    this.tooltipReveal = false;
    this.leaderProgress = 0;
    this.capsuleProgress = 0;
    this.nodes.forEach((n) => {
      n.el.classList.remove('is-active', 'is-dimmed');
      n.el.setAttribute('aria-expanded', 'false');
    });
    this.tooltip.classList.remove('is-open', 'is-down');
    this.tooltip.hidden = true;
    if (this.leader) {
      this.leader.style.removeProperty('transform');
      this.leader.style.removeProperty('opacity');
      this.leader.style.removeProperty('clip-path');
      this.leader.style.removeProperty('-webkit-clip-path');
    }
    if (this.capsule) {
      this.capsule.style.removeProperty('opacity');
      this.capsule.style.removeProperty('transform');
    }
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  updateTooltipReveal(deltaFactor = 1) {
    if (!this.tooltipReveal || !this.activeId) return;

    const leaderSpeed = 0.14 * deltaFactor;
    const capsuleSpeed = 0.11 * deltaFactor;
    this.leaderProgress = Math.min(1, this.leaderProgress + leaderSpeed);
    if (this.leaderProgress > 0.35) {
      this.capsuleProgress = Math.min(1, this.capsuleProgress + capsuleSpeed);
    }

    const active = this.nodes.find((n) => n.id === this.activeId);
    if (active) this.positionTooltip(active);
  }

  positionTooltip(node) {
    const leaderLen = Math.min(88, Math.max(48, this.height * 0.09));
    const edgePad = 12;
    const goDown = node.y < this.height * 0.38;
    this.tooltip.classList.toggle('is-down', goDown);

    const x = node.x;
    const y = node.y;
    const leaderDraw = this.easeOutCubic(this.leaderProgress);
    const capsuleDraw = this.easeOutCubic(this.capsuleProgress);

    this.leader.style.left = `${x}px`;
    if (goDown) {
      this.leader.style.top = `${y}px`;
      this.leader.style.height = `${leaderLen}px`;
      this.capsule.style.top = `${y + leaderLen}px`;
      // Grow from globe (top) downward — avoid scaleY (janky in Safari).
      this.leader.style.clipPath = `inset(0 0 ${(1 - leaderDraw) * 100}% 0)`;
      this.leader.style.webkitClipPath = `inset(0 0 ${(1 - leaderDraw) * 100}% 0)`;
    } else {
      this.leader.style.top = `${y - leaderLen}px`;
      this.leader.style.height = `${leaderLen}px`;
      this.capsule.style.top = `${y - leaderLen}px`;
      // Grow from globe (bottom) upward.
      this.leader.style.clipPath = `inset(${(1 - leaderDraw) * 100}% 0 0 0)`;
      this.leader.style.webkitClipPath = `inset(${(1 - leaderDraw) * 100}% 0 0 0)`;
    }
    this.leader.style.opacity = String(Math.min(1, 0.35 + leaderDraw * 0.65));
    this.leader.style.transform = 'translate3d(-50%, 0, 0)';

    // Center on globe, then clamp so the capsule stays inside the stage.
    this.capsule.style.left = `${x}px`;
    const capsuleWidth = this.capsule.offsetWidth || 0;
    const half = capsuleWidth / 2;
    let left = x;
    if (capsuleWidth > 0) {
      const minLeft = edgePad + half;
      const maxLeft = this.width - edgePad - half;
      if (minLeft <= maxLeft) {
        left = Math.min(Math.max(x, minLeft), maxLeft);
      } else {
        left = this.width / 2;
      }
    }
    this.capsule.style.left = `${left}px`;

    const slide = (1 - capsuleDraw) * (goDown ? -0.35 : 0.35);
    if (goDown) {
      this.capsule.style.transform = `translate3d(-50%, 0, 0) translateY(${slide}rem)`;
    } else {
      this.capsule.style.transform = `translate3d(-50%, -100%, 0) translateY(${slide}rem)`;
    }
    this.capsule.style.opacity = String(capsuleDraw);
  }

  start() {
    if (this.rafId !== null || this.isPaused || this.reducedMotion) {
      if (this.reducedMotion) {
        this.drawStarsFrame(true);
        if (this.activeId) {
          this.leaderProgress = 1;
          this.capsuleProgress = 1;
          const active = this.nodes.find((n) => n.id === this.activeId);
          if (active) this.positionTooltip(active);
        }
      }
      return;
    }
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  tick(timestamp) {
    this.rafId = null;
    if (!this.isVisible) return;
    const last = this._lastTick || timestamp;
    const deltaFactor = Math.min(2, Math.max(0.5, (timestamp - last) / 16.67));
    this._lastTick = timestamp;
    this.time = timestamp;
    this.updateGlobeDrift();
    this.updateTooltipReveal(deltaFactor);
    this.drawStarsFrame(false);
    this.rafId = requestAnimationFrame(this.tick);
  }

  drawStarsFrame(staticFrame) {
    if (!this.ctx) return;
    this.ctx.clearRect(0, 0, this.width, this.height);

    const { r, g, b } = this.starRgb;
    const t = staticFrame ? 0 : this.time * this.config.floatSpeed * this.config.animationSpeed;

    if (this.hasPointer && !staticFrame) {
      this.smoothPointerX += (this.pointerX - this.smoothPointerX) * 0.08;
      this.smoothPointerY += (this.pointerY - this.smoothPointerY) * 0.08;
    } else {
      this.smoothPointerX += (this.width * 0.5 - this.smoothPointerX) * 0.04;
      this.smoothPointerY += (this.height * 0.5 - this.smoothPointerY) * 0.04;
    }

    const pointerDeltaX = this.smoothPointerX - this.width * 0.5;
    const pointerDeltaY = this.smoothPointerY - this.height * 0.5;

    this.stars.forEach((star) => {
      if (!staticFrame && !this.reducedMotion) {
        const time = t * star.speedMult;
        const primaryX = Math.sin(time + star.phaseX) * star.wanderRadius;
        const primaryY = Math.cos(time * 0.82 + star.phaseY) * star.wanderRadius;
        const secondaryX =
          Math.sin(time * 1.41 + star.phaseX * 1.7) * star.wanderRadius * 0.38;
        const secondaryY =
          Math.cos(time * 1.18 + star.phaseY * 2.1) * star.wanderRadius * 0.32;

        star.offsetX = (primaryX + secondaryX) * star.depth;
        star.offsetY = (primaryY + secondaryY) * star.depth;

        star.targetOffsetX = pointerDeltaX * this.config.mouseInfluence * star.depth * 0.35;
        star.targetOffsetY = pointerDeltaY * this.config.mouseInfluence * star.depth * 0.35;
        star.currentOffsetX += (star.targetOffsetX - star.currentOffsetX) * 0.08;
        star.currentOffsetY += (star.targetOffsetY - star.currentOffsetY) * 0.08;
      } else {
        star.offsetX = 0;
        star.offsetY = 0;
        star.currentOffsetX = 0;
        star.currentOffsetY = 0;
      }

      const x = star.baseX + star.offsetX + star.currentOffsetX;
      const y = star.baseY + star.offsetY + star.currentOffsetY;
      const radius = star.radius;
      const glowRadius = radius * (1.6 + this.config.glowStrength * 0.55);

      const glow = this.ctx.createRadialGradient(x, y, 0, x, y, glowRadius);
      glow.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${star.opacity})`);
      glow.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      this.ctx.beginPath();
      this.ctx.fillStyle = glow;
      this.ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.beginPath();
      this.ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${Math.min(1, star.opacity + 0.25)})`;
      this.ctx.arc(x, y, Math.max(0.4, radius * 0.55), 0, Math.PI * 2);
      this.ctx.fill();
    });
  }
}

class HeroTooltipMapComponent extends HTMLElement {
  connectedCallback() {
    if (this._map) return;
    this._map = new HeroTooltipMap(this);
    this._map.init();
  }

  disconnectedCallback() {
    this._map?.destroy();
    this._map = null;
  }
}

if (!customElements.get('hero-tooltip-map-component')) {
  customElements.define('hero-tooltip-map-component', HeroTooltipMapComponent);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('hero-tooltip-map-component').forEach((element) => {
    if (!element._map) {
      element._map = new HeroTooltipMap(element);
      element._map.init();
    } else {
      element._map.onResize();
    }
  });
});

document.addEventListener('shopify:block:select', (event) => {
  const globe = event.target?.closest?.('[data-tooltip-globe]');
  const root = globe?.closest('hero-tooltip-map-component');
  const map = root?._map;
  if (!map || !globe) return;
  const node = map.nodes.find((n) => n.el === globe);
  if (node) map.openTooltip(node);
});

document.addEventListener('shopify:block:deselect', (event) => {
  const root = event.target?.closest?.('hero-tooltip-map-component');
  // Keep a default active star after editor deselect.
  const map = root?._map;
  if (!map) return;
  if (map.nodes.length) map.openTooltip(map.nodes[0], { instant: true });
  else map.closeTooltip();
});
